import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  getFiltersSetOnAssignedCandidates,
  getHMFiltersSetOnAssignedCandidates,
} from '@modules/assigns/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { sortByParamConfig } from '@shared/models/sortby-param';
import { addMonths } from 'date-fns';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { MojoPrivilegesService } from 'mojo-privileges';
import { MojoWebsocketService } from 'mojo-websocket';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  pairwise,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getInnerRouterPath } from '@app/app.reducer';
import {
  associationsGetList,
  associationsGetListSuccess,
  collectionAssociationsClear,
} from '@collections/associations';
import * as FeedbackActions from '@collections/feedback/feedback.actions';
import {
  getJobsSelected,
  getJobsSelectedId,
  JobsActionTypes,
  JobsGet,
  JobsGetSuccess,
  JobsSelectSuccess,
  JobsTeamMembersLoad,
} from '@collections/jobs';
import {
  assignCandidatesLoad,
  assignedRejectedCandidatesLoad,
  associateCandidates,
  associateCandidatesSuccess,
  associationsClear,
  associationStatusConfigurationsLoad,
} from '@modules/assigns/store/assigns.actions';
import {
  jobCandidatesAdvancedLoadSuccess,
  jobCandidatesInReviewLoadSuccess,
  jobCandidatesRejectedLoadSuccess,
  jobHiringManagerCandidatesLoad,
} from '@modules/job-page/store/hiring-manager';
import { sendInviteToApplySuccessClearMatchedCache } from '@shared/invite-to-apply/store/invite-to-apply.actions';
import {
  Candidate,
  HiringManagerAggregation,
  isCountry,
  isDefaultExperienceFilter,
  isState,
  Job,
  Location,
} from '@shared/models';
import { HiringManagerStatus } from '@shared/models/hiring-manager-status';
import { Pagination } from '@shared/pagination';
import { GeolocationService, JobService, SearchCandidatesService } from '@shared/services';
import { AtsReplicationService } from '@shared/services/ats-replication.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { RouteParamsService } from '@shared/services/route-params.service';
import { isTruthy, retrieve } from '@shared/utils';

import { JobConfig } from '../job.abstract.config';
import {
  collectionCandidatesClear,
  jobCandidatesClear,
  jobCandidatesLoad,
  jobCandidatesLoadFail,
  jobCandidatesLoadSuccess,
  jobRefresh,
  jobRefreshSuccess,
  loadDirectCandidatesNumberPerPhase,
  loadDirectCandidatesNumberPerPhaseFail,
  loadDirectCandidatesNumberPerPhaseSuccess,
  shortlistedCandidatesWithAssociationsLoadSuccess,
  loadJobMatchesCounts,
  loadJobMatchesCountsSuccess,
  loadJobMatchesCountsFail,
  jobMatchesNumberForJobSuccess,
} from './job.actions';
import { getJobPageCandidatesState } from './job.selectors';
import {
  DEFAULT_LOCATION_RADIUS_IN_MILES,
  getJobPageFilter,
  initialJobMatchedCandidatesFilterState,
  JobFilterKey,
  jobFilterSet,
  jobFilterSetInitialState,
  jobFilterSetInitialStateSuccess,
  jobFilterStateSuccess,
  jobFilterUpdate,
  JobMatchedCandidatesFilter,
  setInitialAggregationValuesForJob,
} from './matched-candidates-filter';
import { SharedResourcesType } from '@core/shared-resources/shared-resources.model';
import { candidatesExtraInfoLoad } from '@modules/candidate-extra-info/store/candidate-extra-info.actions';
import { sharedResourcesStoreCurrent } from '@core/shared-resources/store/shared-resources.actions';
import { candidatesPageClear } from '@modules/candidates/store';
import { featureFlagsIsActive } from 'mojo-feature-flags';
import { getAggregatedJobDataForJobId } from '@app/collections/aggregations';

const ATS_JOB_UPDATE_EVENT = 'jobsHcmConsumer';

@Injectable()
export class JobEffects {
  setInitialValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobFilterSetInitialState),
      withLatestFrom(this.store.select(getJobsSelected)),
      filter(([_, job]) => !!job),
      concatMap(([{ route, queryParams }, job]) => {
        const { locations: currentLocations, experiences: currentExperiences } = retrieve(
          `${JobFilterKey}-${job.id}`,
          initialJobMatchedCandidatesFilterState,
        ); // TODO: use LocalStorageService
        const experiences =
          isDefaultExperienceFilter(currentExperiences) && !!job.yearsOfExperience
            ? {
                min: job.yearsOfExperience,
                max: this.jobConfig.filter.experience.max,
              }
            : currentExperiences;
        if (!currentLocations && !job.remote) {
          return forkJoin(
            job.locationInput
              .filter(({ name }) => !!name)
              .flatMap((location) =>
                this.geolocationService
                  .autocomplete(location.name)
                  .pipe(
                    map((locations: Location[]) =>
                      locations.filter(
                        (getLocation) =>
                          getLocation.stateCode === location.stateCode &&
                          getLocation.countryCode === location.countryCode &&
                          getLocation.name === location.name,
                      ),
                    ),
                  ),
              ),
          ).pipe(
            map((locations: Location[][]) => ({
              locations: locations.reduce((result, geLocation) => [...result, ...geLocation], []),
              id: job.id,
              experiences,
              route,
              queryParams,
            })),
          );
        } else {
          return of({
            id: job.id,
            locations: currentLocations,
            experiences,
            route,
            queryParams,
          });
        }
      }),
      map(({ id, locations, experiences, route, queryParams }) =>
        jobFilterSetInitialStateSuccess({
          changedInitialState: {
            jobId: id,
            locations,
            experiences,
          },
          route,
          queryParams,
        }),
      ),
    ),
  );

  setInitialFilterState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobFilterSetInitialStateSuccess),

      switchMap(({ changedInitialState, route, queryParams }) => {
        const initialFilterStateWithQueryParamsUpdated = {
          ...initialJobMatchedCandidatesFilterState,
        };
        let jobFilter = {
          // TODO: replace with LocalStorageService
          ...retrieve(
            `${JobFilterKey}-${changedInitialState.jobId}`,
            initialFilterStateWithQueryParamsUpdated,
          ),
          jobId: changedInitialState.jobId,
          locations: changedInitialState.locations,
          experiences: changedInitialState.experiences,
        };
        const { category, offset, ...jobFilterLocalStorage } = jobFilter;

        if (!!!queryParams) {
          jobFilter = {
            ...jobFilter,
            ...this.routeParamsService.mapJSONStringifyToObject(this.route.snapshot.queryParams),
            initialStateLoaded: true,
          };
        }

        this.localStorage.setSavedState(
          jobFilterLocalStorage,
          `${JobFilterKey}-${changedInitialState.jobId}`,
        );

        return [
          jobFilterStateSuccess({ filter: jobFilter }),
          jobFilterSet({ jobFilter, route, queryParams }),
        ];
      }),
    ),
  );

  setInitialAggregationValuesForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setInitialAggregationValuesForJob),
      withLatestFrom(this.store.select(getJobsSelected)),
      switchMap(([_, job]) =>
        this.store.select(getAggregatedJobDataForJobId, { jobId: job.id }).pipe(
          take(1),
          map((information) =>
            jobMatchesNumberForJobSuccess({
              matchesNumbers: information,
            }),
          ),
        ),
      ),
    ),
  );

  filterSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobFilterSet),
      distinctUntilChanged(),
      withLatestFrom(
        this.store.select(getJobsSelected),
        this.store.select(getJobPageFilter),
        this.privilegesService.hasPrivilege(this.jobConfig.privileges.hiringManager),
      ),
      mergeMap(([{ jobFilter, route, queryParams }, job, jobStoreFilters, isHM]) => {
        if (!!queryParams && !!route) {
          this.routeParamsService.updateRouteParams(queryParams, route);
        }
        const initialFilterStateWithPassiveDateUpdated = {
          ...initialJobMatchedCandidatesFilterState,
          passiveModifyDatePeriod: jobStoreFilters.passiveModifyDatePeriod,
          offset: jobStoreFilters.offset,
        };
        let filterResult: JobMatchedCandidatesFilter =
          this.localStorage.getSavedStateWithDefaultValue(
            `${JobFilterKey}-${job.id}`,
            initialFilterStateWithPassiveDateUpdated,
          );

        if (jobFilter.jobId) {
          filterResult = { ...filterResult, ...jobFilter };
          const { category, passiveModifyDatePeriod, offset, ...newFilters } = filterResult;
          this.localStorage.setSavedState(newFilters, `${JobFilterKey}-${jobFilter.jobId}`);
        }

        filterResult = {
          ...filterResult,
          sortBy: jobFilter.sortBy,
          sortByLabel: jobFilter.sortByLabel,
        };

        const sortByFilterOrDefault = this.getSortByFilterOrDefault(filterResult, isHM);
        return [
          jobFilterUpdate({ filter: filterResult }),
          jobCandidatesLoad({
            params: { offset: filterResult.offset, sort: sortByFilterOrDefault },
          }),
        ];
      }),
    ),
  );

  private getSortByFilterOrDefault(filterResult: JobMatchedCandidatesFilter, isHiringManager) {
    return filterResult.sortBy
      ? filterResult.sortBy
      : isHiringManager
      ? sortByParamConfig.applicationDate.sortBy
      : sortByParamConfig.mlScore.sortBy;
  }

  private loadMatchedCandidatesCachedResult$: Observable<Action>;

  clearMatchedCandidatesCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          associateCandidates,
          sendInviteToApplySuccessClearMatchedCache,
          FeedbackActions.feedbackCreateManySuccess,
        ),
        tap(() => (this.loadMatchedCandidatesCachedResult$ = null)),
      ),
    { dispatch: false },
  );

  private loadMatchedCandidatesCountCachedResult$: Observable<Action>;

  loadJobMatchesCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadJobMatchesCounts),
      withLatestFrom(
        this.actions$.pipe(ofType(jobCandidatesLoad)),
        this.store.select(getJobsSelectedId),
        this.store.select(getJobPageFilter),
      ),
      map(([_, { params: payloadParams = { offset: 0 } } = {}, id, filters]) =>
        this.getBodyAndParams(payloadParams, id, filters),
      ),
      startWith(null),
      pairwise(),
      switchMap(([previusQuery, query]) => {
        if (
          JSON.stringify(previusQuery) !== JSON.stringify(query) ||
          !this.loadMatchedCandidatesCountCachedResult$
        ) {
          this.loadMatchedCandidatesCountCachedResult$ = this.searchCandidatesService
            .matchedCount(query)
            .pipe(
              map((response) =>
                loadJobMatchesCountsSuccess({
                  payload: response,
                }),
              ),
              catchError((error) => of(loadJobMatchesCountsFail({ error }))),
              shareReplay(1),
            );
        }
        return this.loadMatchedCandidatesCountCachedResult$;
      }),
    ),
  );

  private getBodyAndParams(payloadParams, id: number, filters: JobMatchedCandidatesFilter) {
    let passiveSinceModifyDate = null;
    if (!!filters.passiveModifyDatePeriod) {
      // filter passive candidates since their last modification date
      // convert numeric period (3,6,12 months) to actual since date
      passiveSinceModifyDate = this.generateISODate(filters.passiveModifyDatePeriod);
    }
    const params = {
      offset: payloadParams.offset ? payloadParams.offset : 0,
      category: filters.category,
      jobId: id,
      sort: payloadParams.sort
        ? payloadParams.sort
        : filters.sortBy || sortByParamConfig.mlScore.sortBy,
    };
    let body: object = {
      keywords: filters.keywords,
      jobTitles: filters.jobTitles,
      passiveSinceModifyDate,
      internalOnTop: filters.internalOnTop,
      referralOnTop: filters.referralOnTop,
      schools: filters.schools,
      fieldOfStudy: filters.fieldOfStudy,
      minimumDegree: filters.minimumDegree,
      companies: filters.companies,
      skills: filters.skills,
    };

    if (filters.enableLocationFilter && !!filters.locations) {
      body = {
        ...body,
        locations: filters.locations,
        locationMilesRadius: filters.locations.some(
          (location) => isCountry(location) || isState(location),
        )
          ? DEFAULT_LOCATION_RADIUS_IN_MILES
          : filters.locationMilesRadius,
      };
    }

    if (filters.enableYOEFilter) {
      body = {
        ...body,
        experiences: filters.experiences,
      };
    }
    return {
      params,
      body,
    };
  }

  loadMatchedCandidates$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(jobCandidatesLoad)),
      this.store.select(getJobsSelectedId),
      this.store.select(getJobPageFilter),
    ]).pipe(
      filter(([_, id, filters]) => !!id && !!filters),
      debounceTime(200),
      map(([{ params: payloadParams = { offset: 0 } } = {}, id, filters]) => ({
        query: this.getBodyAndParams(payloadParams, id, filters),
      })),
      // cache last request and return same action
      // as result if query params won't change
      startWith(null),
      pairwise(),
      switchMap(([previous, current]) => {
        if (
          !current ||
          JSON.stringify(previous?.query) !== JSON.stringify(current.query) ||
          !this.loadMatchedCandidatesCachedResult$
        ) {
          const { query } = current;
          const serviceCall = this.searchCandidatesService.matched(query);

          this.loadMatchedCandidatesCachedResult$ = serviceCall.pipe(
            switchMap((response) =>
              this.searchCandidatesService.matchedCount(query).pipe(
                map((directCandidatesCount) =>
                  jobCandidatesLoadSuccess({
                    payload: response,
                    category: query.params.category,
                    directCandidatesCount,
                  }),
                ),
              ),
            ),
            catchError((error) => of(jobCandidatesLoadFail({ error }))),
            shareReplay(1),
          );
        }
        return this.loadMatchedCandidatesCachedResult$;
      }),
    ),
  );

  loadHiringManagerCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobHiringManagerCandidatesLoad),
      switchMap((payload) =>
        this.store.select(getJobsSelected).pipe(
          first(Boolean),
          map((job) => [payload, job]),
        ),
      ),
      mergeMap(([payload, job]: [any, Job]) => {
        const payloadParams = !!payload ? { ...payload.params } : {};
        const params = {
          offset: payloadParams.offset,
          size: 10,
          jobId: job.id,
          status: payloadParams.status,
          sort: payloadParams.sort ? payloadParams.sort : sortByParamConfig.applicationDate.sortBy,
        };
        return this.searchCandidatesService.hiringManagerAggregation({ ...payload, params }).pipe(
          map((response: HiringManagerAggregation) => {
            if (params.status === HiringManagerStatus.ADVANCED) {
              return jobCandidatesAdvancedLoadSuccess({
                payload: response,
                jobId: job.id,
              });
            }
            if (params.status === HiringManagerStatus.REJECTED) {
              return jobCandidatesRejectedLoadSuccess({
                payload: response,
                jobId: job.id,
              });
            }
            return jobCandidatesInReviewLoadSuccess({
              payload: response,
              jobId: job.id,
            });
          }),
          catchError((error) => of(jobCandidatesLoadFail({ error }))),
        );
      }),
    ),
  );

  refreshHiringManagerCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateCandidatesSuccess),
      switchMap((__) =>
        this.store.select(getInnerRouterPath).pipe(
          map((currentRoute) => {
            if (currentRoute === 'advanced' || currentRoute === 'rejected') {
              const status = currentRoute[0].toUpperCase() + currentRoute.slice(1);
              return jobHiringManagerCandidatesLoad({
                params: { offset: 0, status },
              });
            }
            return jobHiringManagerCandidatesLoad({ params: { offset: 0 } });
          }),
        ),
      ),
    ),
  );

  shortlistedCandidatesWithAssociationsLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsGetListSuccess),
      switchMap(() => of(shortlistedCandidatesWithAssociationsLoadSuccess())),
    ),
  );

  selectJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.Select),
      startWith(null),
      pairwise(),
      switchMap(([id, payload]: [number, { id: number; sort: string | undefined }]) =>
        id !== payload.id
          ? [
              associationsClear(),
              jobCandidatesClear(), // TODO: check if needed
              collectionCandidatesClear(),
              candidatesPageClear(),
              collectionAssociationsClear(),
              new JobsSelectSuccess(payload.id),
              jobCandidatesLoad({ params: { offset: 0, sort: payload.sort } }),
            ]
          : [new JobsSelectSuccess(payload.id)],
      ),
    ),
  );

  selectJobSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.SelectSuccess),
      pluck('payload'),
      filter(isTruthy),
      withLatestFrom(
        this.featureExtensionService.hasFeatureExtension(
          AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
        ),
      ),
      switchMap(([id, hasExtension]: [number, boolean]) => {
        let actions: any[] = [
          new JobsGet({ id }),
          sharedResourcesStoreCurrent({
            resourceId: id,
            resourceType: SharedResourcesType.job,
            teamMembers: [],
          }),
        ];
        if (hasExtension) {
          actions = [...actions, associationStatusConfigurationsLoad({ jobId: id })];
        }
        return actions;
      }),
    ),
  );

  loadCandidateAssociations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(jobCandidatesLoadSuccess),
      pluck('payload'),
      mergeMap((payload: Pagination<Candidate>) =>
        this.store.select(getJobsSelectedId).pipe(
          first(),
          filter(isTruthy),
          map((jobId) => [payload, jobId]),
        ),
      ),
      withLatestFrom(this.privilegesService.hasPrivilege(this.jobConfig.privileges.hiringManager)),
      filter(([_, isHiringManager]) => !isHiringManager),
      switchMap(([[candidatePage, jobId], _]: [[Pagination<Candidate>, number], boolean]) => [
        associationsGetList({ jobId }),
        candidatesExtraInfoLoad({ candidateIds: candidatePage?.content?.map((item) => item.id) }),
      ]),
    ),
  );

  loadDirectCandidatesPerPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDirectCandidatesNumberPerPhase),
      switchMap(({ jobId }) =>
        this.jobService.getDirectPerPhase(jobId).pipe(
          map((directApplicantsPerPhaseNumbers) =>
            loadDirectCandidatesNumberPerPhaseSuccess({
              directApplicantsPerPhaseNumbers,
            }),
          ),
          catchError((error) => of(loadDirectCandidatesNumberPerPhaseFail({ error }))),
        ),
      ),
    ),
  );

  reloadMatchesPerPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateCandidatesSuccess),
      switchMap(() => this.store.select(getJobPageCandidatesState).pipe(first(), filter(isTruthy))),
      withLatestFrom(
        this.store.select(getJobsSelectedId),
        this.store.select(getFiltersSetOnAssignedCandidates),
        this.store.select(getHMFiltersSetOnAssignedCandidates),
      ),
      switchMap(
        ([{ number }, jobId, assignedStatuses, hmFilters]: [
          Pagination,
          number,
          string[],
          string[],
        ]) => [
          jobCandidatesLoad({
            params: { offset: number },
          }),
          loadDirectCandidatesNumberPerPhase({
            jobId,
          }),
          assignCandidatesLoad({
            params: { offset: 0 },
            filters: assignedStatuses,
            hmSelectedStatuses: hmFilters,
          }),
          assignedRejectedCandidatesLoad({
            params: { offset: 0 },
          }),
        ],
      ),
    ),
  );

  private generateISODate(passiveModifyDatePeriod: number) {
    let passiveSinceModifyDate = new Date();
    passiveSinceModifyDate.setHours(0, 0, 0, 0);
    passiveSinceModifyDate = addMonths(passiveSinceModifyDate, -passiveModifyDatePeriod);
    return passiveSinceModifyDate.toISOString();
  }

  refreshJob = createEffect(() =>
    this.actions$.pipe(
      ofType(jobRefresh),
      mergeMap(({ atsMessage }) =>
        this.hcmService.refreshJob(atsMessage).pipe(
          concatMap(
            (message) =>
              new Observable<Action>((subscriber) => {
                this.mojoWebsocketService.registerCallback(
                  ATS_JOB_UPDATE_EVENT,
                  (jobJson: string) => {
                    const job = JSON.parse(jobJson) as Job;
                    subscriber.next(jobRefreshSuccess({ job }));
                    this.mojoWebsocketService.unregisterCallback(ATS_JOB_UPDATE_EVENT);
                  },
                );
              }),
          ),
        ),
      ),
    ),
  );

  refreshJobSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(jobRefreshSuccess),
      concatMap(({ job }) => [new JobsGetSuccess(job), new JobsTeamMembersLoad(job)]),
    ),
  );

  constructor(
    private actions$: Actions,
    private searchCandidatesService: SearchCandidatesService,
    private store: Store<{}>,
    private privilegesService: MojoPrivilegesService,
    private featureExtensionService: MojoFeatureExtensionsService,
    private jobConfig: JobConfig,
    private geolocationService: GeolocationService,
    private localStorage: LocalStorageService,
    private hcmService: AtsReplicationService,
    private route: ActivatedRoute,
    private jobService: JobService,
    private routeParamsService: RouteParamsService,
    private mojoWebsocketService: MojoWebsocketService,
  ) {}
}
